(() => {
    'use strict';

    const root = document.getElementById('article-feedback');
    if (!root) return;

    const api = '/api/article-feedback.php';
    const pageKey = root.dataset.pageKey;
    const canonical = root.dataset.url;
    const status = root.querySelector('[data-feedback-status]');
    const retry = root.querySelector('[data-feedback-retry]');
    const stars = Array.from(root.querySelectorAll('[data-feedback-rate]'));
    const helpful = Array.from(root.querySelectorAll('[data-feedback-helpful]'));
    const forms = Array.from(root.querySelectorAll('[data-feedback-form]'));
    const toast = root.querySelector('[data-feedback-toast]');
    let ready = false;
    let loading = false;
    let voting = false;
    let ownRating = null;
    let ownHelpful = null;
    let toastTimer;
    const tokenKey = 'bronidom:article-feedback:token';
    const ownKey = `bronidom:article-feedback:${pageKey}`;
    let browserToken = '';
    try {
        browserToken = localStorage.getItem(tokenKey) || '';
        const saved = JSON.parse(localStorage.getItem(ownKey) || '{}');
        if (saved?.token === browserToken) {
            ownRating = Number.isInteger(saved.rating) && saved.rating >= 1 && saved.rating <= 5 ? saved.rating : null;
            ownHelpful = ['yes', 'no'].includes(saved.helpful) ? saved.helpful : null;
        }
    } catch { /* Storage is optional; a successful choice stays in this tab. */ }

    const saveOwn = () => {
        try { localStorage.setItem(ownKey, JSON.stringify({ rating: ownRating, helpful: ownHelpful, token: browserToken })); } catch { /* Optional storage. */ }
    };

    const say = (message, error = false) => {
        status.textContent = message;
        status.classList.toggle('error', error);
        status.classList.toggle('success', !error && ready);
    };

    const notify = (message) => {
        clearTimeout(toastTimer);
        toast.textContent = message;
        toast.hidden = false;
        toastTimer = setTimeout(() => { toast.hidden = true; }, 5000);
    };

    const setReady = (value) => {
        ready = value;
        stars.concat(helpful).forEach((button) => { button.disabled = !value || voting; });
        forms.forEach((form) => {
            if (form.dataset.sending !== '1') form.querySelector('[data-feedback-fields]').disabled = !value;
        });
    };

    const rotateCsrf = (response) => {
        const token = response.headers.get('X-CSRF-Token');
        if (!token) return;
        const meta = document.querySelector('meta[name="csrf-token"]');
        if (meta) meta.content = token;
        window.CSRF = token;
        forms.forEach((form) => { form.elements.namedItem('_csrf').value = token; });
    };

    const request = async (action, body) => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 12000);
        try {
            const token = document.querySelector('meta[name="csrf-token"]')?.content || window.CSRF;
            if (body && !token) throw new Error('Обновите страницу, чтобы безопасно отправить ответ.');
            const response = await fetch(body ? api : `${api}?action=summary&page_key=${encodeURIComponent(pageKey)}`, {
                method: body ? 'POST' : 'GET',
                credentials: 'same-origin',
                cache: 'no-store',
                signal: controller.signal,
                headers: { ...(body ? { 'Content-Type': 'application/json' } : {}),
                    ...(browserToken ? { 'X-Article-Feedback-Token': browserToken } : {}) },
                ...(body ? {
                    body: JSON.stringify({ ...body, action, page_key: pageKey, _csrf: token, _hp: body._hp || '' }),
                } : {}),
            });
            rotateCsrf(response);
            const result = await response.json();
            if (response.status === 429) throw new Error('Слишком много запросов. Подождите немного и попробуйте снова.');
            if (response.status === 403) throw new Error('Обновите страницу и попробуйте снова.');
            if (!response.ok || result.ok !== true) throw new Error(typeof result.error === 'string' && result.error.length <= 300
                ? result.error : 'Не удалось сохранить ответ. Попробуйте позже.');
            if (typeof result.token === 'string' && /^[a-f0-9]{32}\.[0-9]{10}\.[a-f0-9]{64}$/.test(result.token)) {
                const changed = browserToken && browserToken !== result.token;
                browserToken = result.token;
                if (changed) {
                    ownRating = null;
                    ownHelpful = null;
                    saveOwn();
                }
                try { localStorage.setItem(tokenKey, browserToken); } catch { /* Optional storage. */ }
            }
            return result;
        } catch (error) {
            if (error instanceof TypeError || error instanceof SyntaxError || error.name === 'AbortError') {
                throw new Error('Не удалось связаться с сайтом. Проверьте соединение и попробуйте снова.');
            }
            throw error;
        } finally {
            clearTimeout(timeout);
        }
    };

    const preview = (rating) => {
        stars.forEach((button) => {
            button.classList.toggle('preview', rating !== null);
            button.classList.toggle('active', rating !== null && Number(button.dataset.feedbackRate) <= rating);
        });
    };

    const render = (result) => {
        const summary = result.summary;
        if (!summary || !Number.isFinite(summary.average) || summary.average < 0 || summary.average > 5
            || ![summary.count, summary.yes, summary.no].every((count) => Number.isInteger(count) && count >= 0)) {
            throw new Error('Не удалось загрузить оценки. Попробуйте снова.');
        }
        root.querySelector('[data-feedback-average]').textContent = summary.count ? summary.average.toFixed(1).replace('.', ',') : '—';
        root.querySelector('[data-feedback-count]').textContent = String(summary.count);
        const lastTwo = summary.count % 100;
        const last = summary.count % 10;
        root.querySelector('[data-feedback-count-label]').textContent = lastTwo >= 11 && lastTwo <= 14 ? 'голосов' : last === 1 ? 'голос' : last >= 2 && last <= 4 ? 'голоса' : 'голосов';
        root.querySelector('[data-feedback-yes]').textContent = String(summary.yes);
        root.querySelector('[data-feedback-no]').textContent = String(summary.no);
        stars.forEach((button) => {
            const value = Number(button.dataset.feedbackRate);
            button.dataset.fill = String(Math.round(Math.max(0, Math.min(1, summary.average - value + 1)) * 10));
            button.setAttribute('aria-pressed', String(value === ownRating));
        });
        helpful.forEach((button) => { button.setAttribute('aria-pressed', String(button.dataset.feedbackHelpful === ownHelpful)); });
        preview(null);
    };

    const hydrate = async () => {
        if (loading || root.dataset.available !== '1') return;
        loading = true;
        retry.hidden = true;
        setReady(false);
        say('Загружаем ваши оценки…');
        try {
            render(await request('summary'));
            setReady(true);
            say(ownRating || ownHelpful ? 'Ваш ответ сохранён. Вы можете изменить его.' : 'Оцените статью — ваш голос поможет другим.');
        } catch (error) {
            say(error.message, true);
            // 16.10.2026: подпись кнопки — только в момент показа (скрытая надпись в
            // серверном HTML уходила в сниппет Яндекса; HiddenServiceTextTest).
            retry.textContent = 'Повторить загрузку';
            retry.hidden = false;
        } finally {
            loading = false;
        }
    };

    const vote = async (action, body) => {
        if (!ready || voting) return;
        voting = true;
        setReady(true);
        root.querySelector('.article-feedback-panel').setAttribute('aria-busy', 'true');
        try {
            const result = await request(action, body);
            render(result);
            if (action === 'rate') ownRating = body.rating; else ownHelpful = body.helpful;
            saveOwn();
            render(result);
            say(action === 'rate' ? 'Спасибо! Ваша оценка сохранена.' : 'Спасибо! Ваш ответ поможет сделать материал полезнее.');
            status.classList.remove('article-feedback-thanks');
            void status.offsetWidth;
            status.classList.add('article-feedback-thanks');
        } catch (error) {
            preview(null);
            say(error.message, true);
        } finally {
            voting = false;
            setReady(true);
            root.querySelector('.article-feedback-panel').removeAttribute('aria-busy');
        }
    };

    stars.forEach((button, index) => {
        button.addEventListener('pointerenter', () => { if (ready && !voting) preview(index + 1); });
        button.addEventListener('focus', () => { if (ready && !voting) preview(index + 1); });
        button.addEventListener('click', () => { vote('rate', { rating: index + 1 }); });
        button.addEventListener('keydown', (event) => {
            const shift = ['ArrowRight', 'ArrowUp'].includes(event.key) ? 1 : ['ArrowLeft', 'ArrowDown'].includes(event.key) ? -1 : 0;
            if (!shift && event.key !== 'Home' && event.key !== 'End') return;
            event.preventDefault();
            const next = event.key === 'Home' ? 0 : event.key === 'End' ? 4 : (index + shift + stars.length) % stars.length;
            stars[next].focus();
        });
    });
    const starGroup = root.querySelector('[data-feedback-stars]');
    starGroup.addEventListener('pointerleave', () => { preview(null); });
    starGroup.addEventListener('focusout', (event) => { if (!starGroup.contains(event.relatedTarget)) preview(null); });
    helpful.forEach((button) => { button.addEventListener('click', () => { vote('helpful', { helpful: button.dataset.feedbackHelpful }); }); });
    retry.addEventListener('click', hydrate);

    const bookmark = root.querySelector('[data-feedback-bookmark]');
    const bookmarkKey = `bronidom:article-bookmark:${pageKey}`;
    const showBookmark = (saved) => {
        bookmark.setAttribute('aria-pressed', String(saved));
        bookmark.setAttribute('aria-label', saved ? 'Удалить закладку из этого браузера' : 'Сохранить статью в этом браузере');
        bookmark.title = saved ? 'Статья сохранена в этом браузере' : 'Сохранить в этом браузере';
    };
    try { showBookmark(localStorage.getItem(bookmarkKey) !== null); } catch { showBookmark(false); }
    bookmark.addEventListener('click', () => {
        try {
            const saved = localStorage.getItem(bookmarkKey) !== null;
            if (saved) localStorage.removeItem(bookmarkKey); else localStorage.setItem(bookmarkKey, canonical);
            showBookmark(!saved);
            notify(saved ? 'Закладка удалена' : 'Сохранено в этом браузере. При возвращении закладка останется отмеченной.');
        } catch {
            notify('Браузер не разрешил сохранить закладку. Добавьте страницу в избранное через его меню.');
        }
    });
    root.querySelector('[data-feedback-print]').addEventListener('click', () => { window.print(); });
    let printDetails = [];
    let printActive = false;
    window.addEventListener('beforeprint', () => {
        if (printActive) return;
        printActive = true;
        printDetails = Array.from(document.querySelectorAll('details')).map((details) => ({ details, open: details.open }));
        printDetails.forEach(({ details }) => { details.open = true; });
    });
    window.addEventListener('afterprint', () => {
        if (!printActive) return;
        printDetails.forEach(({ details, open }) => { details.open = open; });
        printDetails = [];
        printActive = false;
    });

    const copyLink = async () => {
        try {
            if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
            await navigator.clipboard.writeText(canonical);
            notify('Скопировано');
        } catch {
            const copy = root.querySelector('[data-feedback-copy]');
            copy.hidden = false;
            const input = copy.querySelector('input');
            input.focus();
            input.select();
            notify('Скопируйте выделенную ссылку.');
        }
    };
    root.querySelector('[data-feedback-share]').addEventListener('click', async () => {
        if (typeof navigator.share === 'function') {
            try {
                await navigator.share({ title: root.dataset.title, url: canonical });
                return;
            } catch (error) {
                if (error.name === 'AbortError') return;
            }
        }
        await copyLink();
    });

    forms.forEach((form) => {
        const formStatus = root.querySelector(`[data-feedback-form-status="${form.dataset.feedbackForm}"]`);
        const fields = form.querySelector('[data-feedback-fields]');
        const reviewOptions = Array.from(form.querySelectorAll('input[name="rating"]'));
        const showReviewStars = (value) => {
            reviewOptions.forEach((radio) => { radio.closest('label').classList.toggle('active', Number(radio.value) <= value); });
        };
        reviewOptions.forEach((radio) => {
            radio.addEventListener('change', () => { showReviewStars(Number(radio.value)); });
            radio.closest('label').addEventListener('pointerenter', () => { showReviewStars(Number(radio.value)); });
        });
        form.querySelector('[data-feedback-review-stars]')?.addEventListener('pointerleave', () => {
            showReviewStars(Number(form.querySelector('input[name="rating"]:checked')?.value || 0));
        });
        form.addEventListener('submit', async (event) => {
            event.preventDefault();
            if (!ready || form.dataset.sending === '1' || !form.reportValidity()) return;
            const data = new FormData(form);
            const consent = data.get('consent') === '1';
            if (!consent) return;
            const body = { name: String(data.get('name') || '').trim(), text: String(data.get('text') || '').trim(), consent, _hp: String(data.get('_hp') || '') };
            if (form.dataset.feedbackForm === 'review') body.rating = Number(data.get('rating'));
            form.dataset.sending = '1';
            fields.disabled = true;
            form.setAttribute('aria-busy', 'true');
            formStatus.hidden = false;
            formStatus.classList.remove('error', 'success');
            formStatus.textContent = 'Отправляем на проверку…';
            try {
                await request(form.dataset.feedbackForm, body);
                form.hidden = true;
                formStatus.classList.add('success', 'article-feedback-thanks');
                formStatus.textContent = form.dataset.feedbackForm === 'review'
                    ? 'Спасибо за отзыв! Он появится после одобрения владельцем.'
                    : 'Спасибо за вопрос! Он появится вместе с ответом после проверки.';
            } catch (error) {
                formStatus.classList.add('error');
                formStatus.textContent = error.message;
            } finally {
                form.dataset.sending = '0';
                fields.disabled = !ready;
                form.removeAttribute('aria-busy');
                formStatus.focus();
            }
        });
    });

    root.querySelectorAll('.article-feedback-question').forEach((details) => {
        details.addEventListener('toggle', () => { details.classList.toggle('active', details.open); });
    });
    setReady(false);
    hydrate();
})();
